import axios, { AxiosResponse } from 'axios';
import {
  FMRLookupResponse,
  FMRSearchResponse,
  FMRAvailableYearsResponse,
  FMRStatesResponse,
  ApiError
} from '../types';

// Create axios instance with base configuration - use relative URL for Apache proxy
const api = axios.create({
  baseURL: '/api',
  timeout: 30000,
  withCredentials: true,
  // Double-submit CSRF: the server checks this header against the session.
  xsrfCookieName: 'fmr8_csrf',
  xsrfHeaderName: 'x-csrf-token',
  headers: {
    'Content-Type': 'application/json',
  },
});

/** The server's error body for a failed request, or a generic network error. */
function toApiError(error: unknown): ApiError {
  if (axios.isAxiosError(error) && error.response) {
    return error.response.data as ApiError;
  }
  return { error: 'Network error occurred' };
}

/** Admin calls resolve with the error instead of throwing it. */
function toAdminErrorResult(error: unknown): { success: boolean; error: string } {
  if (axios.isAxiosError(error) && error.response) {
    return error.response.data as { success: boolean; error: string };
  }
  return { success: false, error: 'Network error occurred' };
}

/**
 * FMR API Service Class
 */
export class FMRApiService {
  /**
   * Lookup FMR data by ZIP code
   */
  static async lookupByZipCode(
    zipCode: string,
    year?: number
  ): Promise<FMRLookupResponse> {
    try {
      const params = year ? { year: year.toString() } : {};
      const response: AxiosResponse<FMRLookupResponse> = await api.get(
        `/fmr/lookup/${zipCode}`,
        { params }
      );
      return response.data;
    } catch (error) {
      throw toApiError(error);
    }
  }

  /**
   * Search FMR data with filters
   */
  static async searchFMR(
    filters: {
      state?: string;
      county?: string;
      year?: number;
      limit?: number;
    }
  ): Promise<FMRSearchResponse> {
    try {
      const params = new URLSearchParams();
      
      if (filters.state) params.append('state', filters.state);
      if (filters.county) params.append('county', filters.county);
      if (filters.year) params.append('year', filters.year.toString());
      if (filters.limit) params.append('limit', filters.limit.toString());

      const response: AxiosResponse<FMRSearchResponse> = await api.get(
        `/fmr/search?${params.toString()}`
      );
      return response.data;
    } catch (error) {
      throw toApiError(error);
    }
  }

  /**
   * Get available years for a ZIP code
   */
  static async getAvailableYears(zipCode: string): Promise<FMRAvailableYearsResponse> {
    try {
      const response: AxiosResponse<FMRAvailableYearsResponse> = await api.get(
        `/fmr/years/${zipCode}`
      );
      return response.data;
    } catch (error) {
      throw toApiError(error);
    }
  }

  /**
   * Get list of available states
   */
  static async getStates(): Promise<FMRStatesResponse> {
    try {
      const response: AxiosResponse<FMRStatesResponse> = await api.get('/fmr/states');
      return response.data;
    } catch (error) {
      throw toApiError(error);
    }
  }

  /**
   * Get AI-generated ZIP content (neighboring zips + market analysis)
   */
  static async getZipContent(zipCode: string): Promise<{
    success: boolean;
    zipCode: string;
    neighboringZipsHtml: string;
    marketAnalysisHtml: string;
    fromCache: boolean;
  }> {
    try {
      const response = await api.get(`/fmr/zipcontent/${zipCode}`);
      return response.data;
    } catch (error) {
      throw toApiError(error);
    }
  }

  /**
   * Get city FMR data (both FY 2026 and FY 2027)
   */
  static async getCityData(cityName: string, state: string): Promise<{
    success: boolean;
    cityInfo: {
      city: string;
      county: string;
      state: string;
      stateAbbr: string;
    };
    zipCodes: string[];
    data: {
      fy2026: any[];
      fy2027: any[];
    };
    count: number;
    error?: string;
  }> {
    try {
      const response = await api.get(`/fmr/city/${encodeURIComponent(cityName)}/${encodeURIComponent(state)}`);
      return response.data;
    } catch (error) {
      throw toApiError(error);
    }
  }

  /**
   * Get city investor overview content
   */
  static async getCityContent(cityName: string, state: string): Promise<{
    success: boolean;
    cityName: string;
    stateAbbr: string;
    county: string;
    investorOverviewHtml: string;
    metaDescription?: string;
    zipCodes: string[];
    fromCache: boolean;
    error?: string;
  }> {
    try {
      const response = await api.get(`/fmr/citycontent/${encodeURIComponent(cityName)}/${encodeURIComponent(state)}`);
      return response.data;
    } catch (error) {
      throw toApiError(error);
    }
  }

  /**
   * Autocomplete search for ZIP codes and cities
   */
  static async autocomplete(query: string, limit: number = 10): Promise<{
    success: boolean;
    results: Array<{
      type: 'zipcode' | 'city';
      zipCode: string;
      city: string;
      county: string;
      state: string;
      stateAbbr: string;
      label: string;
    }>;
    count: number;
  }> {
    try {
      const response = await api.get(`/fmr/autocomplete?q=${encodeURIComponent(query)}&limit=${limit}`);
      return response.data;
    } catch (error) {
      throw toApiError(error);
    }
  }

  /**
   * Get all states with aggregate FMR data
   */
  static async getStatesDirectory(): Promise<{
    success: boolean;
    states: Array<{
      state: string;
      zipCount: number;
      countyCount: number;
      cityCount: number;
      avgFmr: { studio: number; br1: number; br2: number; br3: number; br4: number };
      fmrRange: { min: number; max: number };
    }>;
    count: number;
  }> {
    try {
      const response = await api.get('/fmr/states/directory');
      return response.data;
    } catch (error) {
      throw toApiError(error);
    }
  }

  /**
   * Get state data with counties and top cities
   */
  static async getStateData(stateCode: string): Promise<{
    success: boolean;
    state: string;
    aggregate: {
      zipCount: number;
      countyCount: number;
      avgFmr: { studio: number; br1: number; br2: number; br3: number; br4: number };
      fmrRange: { min: number; max: number };
    };
    counties: Array<{
      county: string;
      zipCount: number;
      cityCount: number;
      avgFmr: { studio: number; br1: number; br2: number; br3: number; br4: number };
      fmrRange: { min: number; max: number };
    }>;
    topCities: Array<{
      city: string;
      county: string;
      zipCount: number;
      avgBr2: number;
    }>;
  }> {
    try {
      const response = await api.get(`/fmr/state/${encodeURIComponent(stateCode)}`);
      return response.data;
    } catch (error) {
      throw toApiError(error);
    }
  }

  /**
   * Get county data with all ZIP codes
   */
  static async getCountyData(countyName: string, stateCode: string): Promise<{
    success: boolean;
    county: string;
    state: string;
    metropolitanArea: string;
    cities: string[];
    aggregate: {
      zipCount: number;
      cityCount: number;
      avgFmr: { studio: number; br1: number; br2: number; br3: number; br4: number };
    };
    zipCodes: Array<{
      zipCode: string;
      city: string;
      bedroom0: number;
      bedroom1: number;
      bedroom2: number;
      bedroom3: number;
      bedroom4: number;
    }>;
  }> {
    try {
      const response = await api.get(`/fmr/county/${encodeURIComponent(countyName)}/${encodeURIComponent(stateCode)}`);
      return response.data;
    } catch (error) {
      throw toApiError(error);
    }
  }

  /**
   * Get bedroom curve data for charts (rent and YoY growth by bedroom size)
   */
  static async getBedroomCurve(zipCode: string): Promise<{
    success: boolean;
    zipCode: string;
    data: Array<{
      bedroom: string;
      rent: number;
      yoyGrowth: number;
    }>;
  }> {
    try {
      const response = await api.get(`/fmr/bedroom-curve/${zipCode}`);
      return response.data;
    } catch (error) {
      throw toApiError(error);
    }
  }

  /**
   * Get all glossary terms (index data)
   */
  static async getGlossaryTerms(): Promise<{
    success: boolean;
    terms: Array<{
      slug: string;
      term: string;
      shortDefinition: string;
      category: string;
      relatedTerms: string[];
    }>;
    count: number;
  }> {
    try {
      const response = await api.get('/fmr/glossary');
      return response.data;
    } catch (error) {
      throw toApiError(error);
    }
  }

  /**
   * Get a single glossary term with full content
   */
  static async getGlossaryTerm(slug: string): Promise<{
    success: boolean;
    term: {
      slug: string;
      term: string;
      shortDefinition: string;
      fullDefinition: string;
      category: string;
      relatedTerms: string[];
      faqItems: Array<{ question: string; answer: string }>;
    };
  }> {
    try {
      const response = await api.get(`/fmr/glossary/${encodeURIComponent(slug)}`);
      return response.data;
    } catch (error) {
      throw toApiError(error);
    }
  }

  /**
   * Get historical trends data for charts (FMR by bedroom size over years)
   */
  static async getHistoricalTrends(zipCode: string): Promise<{
    success: boolean;
    zipCode: string;
    years: number[];
    data: {
      studio: number[];
      br1: number[];
      br2: number[];
      br3: number[];
      br4: number[];
    };
    count: number;
  }> {
    try {
      const response = await api.get(`/fmr/historical-trends/${zipCode}`);
      return response.data;
    } catch (error) {
      throw toApiError(error);
    }
  }
}

/**
 * Admin API Service Class
 */
export class AdminApiService {
  private static token: string | null = null;

  /**
   * Set authentication token
   */
  static setToken(token: string) {
    this.token = token;
    api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
  }

  /**
   * Clear authentication token
   */
  static clearToken() {
    this.token = null;
    delete api.defaults.headers.common['Authorization'];
  }

  /**
   * Check if user is authenticated
   */
  static isAuthenticated(): boolean {
    return this.token !== null;
  }

  /**
   * Admin login
   */
  static async login(password: string): Promise<{ success: boolean; token?: string; error?: string }> {
    try {
      const response: AxiosResponse<{ success: boolean; token?: string; error?: string }> = 
        await api.post('/admin/login', { password });
      return response.data;
    } catch (error) {
      return toAdminErrorResult(error);
    }
  }

  /**
   * Upload FMR file
   */
  static async uploadFMRFile(
    file: File,
    year: number
  ): Promise<{ success: boolean; message?: string; error?: string }> {
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('year', year.toString());

      const response: AxiosResponse<{ success: boolean; message?: string; error?: string }> = 
        await api.post('/admin/upload', formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        });
      return response.data;
    } catch (error) {
      return toAdminErrorResult(error);
    }
  }

  /**
   * Get admin status
   */
  static async getStatus(): Promise<{ success: boolean; database?: string; error?: string }> {
    try {
      const response: AxiosResponse<{ success: boolean; database?: string; error?: string }> = 
        await api.get('/admin/status');
      return response.data;
    } catch (error) {
      return toAdminErrorResult(error);
    }
  }

  /**
   * Get available years in database
   */
  static async getYears(): Promise<{ success: boolean; years?: number[]; error?: string }> {
    try {
      const response: AxiosResponse<{ success: boolean; years?: number[]; error?: string }> = 
        await api.get('/admin/years');
      return response.data;
    } catch (error) {
      return toAdminErrorResult(error);
    }
  }
}

export default api;
